import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import { BLOG_HOME_PAGE } from 'shared/pageTypes';
import { toggleBlogSettings } from 'web/store/modules/site';
import { create, makeLive, makeDraft } from 'web/store/modules/pages';
import { DEFAULT_LOCALE } from '../../../../modules/i18n/locales';
import useQuery from '../../../../modules/react/hooks/useQuery';
import useMutation from '../../../../modules/react/hooks/useMutation';
import ToggleButton from '../components/ToggleButton';

const GET_BLOG_DATA = gql`
  query GetCurrentSiteBlogData($locale: String!) {
    currentSite {
      id
      ...CurrentSiteBlogDataToggleButton
    }
  }

  ${ToggleButton.fragments.currentSite}
`;

const TOGGLE_BLOG = gql`
  mutation ToggleBlogActivation($input: SiteBlogSettingsInput!, $locale: String!) {
    currentSiteBlogSettingsUpdate(input: $input) {
      currentSite {
        id
        ...CurrentSiteBlogDataToggleButton
      }
    }
  }

  ${ToggleButton.fragments.currentSite}
`;

const propTypes = {
  toggleSettings: PropTypes.func.isRequired(),
  endraftenBlogHomepage: PropTypes.func.isRequired(),
  enlivenBlogHomepage: PropTypes.func.isRequired(),
  createBlogHomepage: PropTypes.func.isRequired(),
};

const defaultProps = {};

const BuilderBlogActiveToggle = ({
  toggleSettings,
  endraftenBlogHomepage,
  enlivenBlogHomepage,
  createBlogHomepage,
}) => {
  const { data, loading } = useQuery(GET_BLOG_DATA, { variables: { locale: DEFAULT_LOCALE } });
  const [
    toggleBlog,
    { loading: toggling },
  ] = useMutation(TOGGLE_BLOG, { refetchQueries: ['GetCurrentSiteBlogData'] });

  if (!data) {
    return null;
  }

  const onClickToggle = async (enabled) => {
    const { currentSite: { blogSettings: { homepage } } } = data;
    if (!enabled && homepage && homepage.live) {
      endraftenBlogHomepage(homepage);
    } else if (enabled && homepage && !homepage.live) {
      enlivenBlogHomepage(homepage);
    } else {
      const { data: { page: blogHomepage } } = await createBlogHomepage(BLOG_HOME_PAGE);
      enlivenBlogHomepage(blogHomepage);
    }

    toggleSettings();

    await toggleBlog({ variables: {
      input: {
        blogSettings: {
          enabled,
        },
      },
      locale: DEFAULT_LOCALE,
    } });
  };

  return (
    <ToggleButton
      loading={loading}
      site={data.currentSite}
      toggleBlog={onClickToggle}
      toggling={toggling}
    />
  );
};

BuilderBlogActiveToggle.propTypes = propTypes;
BuilderBlogActiveToggle.defaultProps = defaultProps;

BuilderBlogActiveToggle.fragments = {};

/*
 * When the blog is toggled between enabled/disabled, dispatch the Redux actions to load the app and
 * create/enliven/endraften the blog homepage to ensure the 'ViewLiveButton' is updated in the builder.
 * When the CurrentSiteContext is updated to pull data from Apollo GraphQL/V2 API
 * instead Redux/V1 API we can get rid of `toggleBlogSettings`.
 * When relevant v2 mutations to create/enliven/endraften pages are created, we can get remove the
 * the relevant pages Redux actions found here.
 */
export default connect(
  null,
  {
    toggleSettings: toggleBlogSettings,
    createBlogHomepage: create,
    enlivenBlogHomepage: makeLive,
    endraftenBlogHomepage: makeDraft,
  },
)(BuilderBlogActiveToggle);
